import React from 'react';
import { ArrowLeft, Users, Handshake, Shield, Heart, Quote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logoUrl } from '../mockData';

const SocietaCooperativa = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-[#1B2380] hover:text-[#0E8C84] transition-colors duration-300"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Torna alla Home</span>
            </button>
            <img src={logoUrl} alt="Assietica" className="h-12 w-auto" />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#0E8C84] to-[#0E8C84] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-sm font-semibold mb-4">
            Anima Cooperativa
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Perché siamo una Società Cooperativa
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            La forza dell'unione e della mutualità.
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
          
          {/* Intro */}
          <div className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed">
              Abbiamo scelto la forma di <strong className="text-[#0E8C84]">Società Cooperativa</strong> perché crediamo che la cooperazione 
              sia il modello più democratico ed efficace per gestire servizi delicati come quelli assicurativi. 
              In una cooperativa, <strong className="text-[#1B2380]">la persona viene prima del capitale</strong>.
            </p>
          </div>

          {/* Perché questa scelta */}
          <section className="mb-12">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-[#0E8C84]/10 rounded-xl flex items-center justify-center">
                <Handshake className="text-[#0E8C84]" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#0E8C84]">Perché questa scelta?</h2>
            </div>
            
            <div className="space-y-6">
              {/* Mutualità */}
              <div className="bg-[#0E8C84]/5 rounded-xl p-6 border-l-4 border-[#0E8C84]">
                <h3 className="text-xl font-bold text-[#0E8C84] mb-3">Mutualità</h3>
                <p className="text-gray-700 leading-relaxed">
                  Il nostro obiettivo non è la speculazione, ma il <strong>soddisfacimento dei bisogni dei soci e dei clienti</strong> attraverso 
                  condizioni eque e servizi altamente specializzati.
                </p>
              </div>
              
              {/* Democrazia Partecipativa */}
              <div className="bg-[#1B2380]/5 rounded-xl p-6 border-l-4 border-[#1B2380]">
                <h3 className="text-xl font-bold text-[#1B2380] mb-3">Democrazia Partecipativa</h3>
                <p className="text-gray-700 leading-relaxed">
                  Essere una cooperativa significa favorire la <strong>partecipazione e la trasparenza</strong>. Ogni scelta è orientata 
                  alla continuità e alla solidità del servizio nel tempo, a tutela di chi ci dà fiducia.
                </p>
              </div>
            </div>
          </section>

          {/* Obiettivo Sociale */}
          <section className="mb-12">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-[#0E8C84]/10 rounded-xl flex items-center justify-center">
                <Users className="text-[#0E8C84]" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#0E8C84]">Qual è l'obiettivo sociale?</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Creare una <strong>rete di protezione</strong> in cui il valore generato torni alla comunità sotto forma di servizi migliori, 
              consulenza etica e sostegno alle realtà associative. Assietica è, a tutti gli effetti, 
              <strong className="text-[#0E8C84]"> una comunità che protegge le comunità</strong>.
            </p>
          </section>

          {/* A cosa serve */}
          <section className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-[#E67E22]/10 rounded-xl flex items-center justify-center">
                <Shield className="text-[#E67E22]" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#E67E22]">A cosa serve?</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-6">
              Serve a garantire che il cliente non sia mai un "numero", ma un <strong>membro di un progetto condiviso</strong>. 
              In un mercato assicurativo spesso impersonale, la forma cooperativa ci permette di mantenere un 
              <strong className="text-[#1B2380]"> legame diretto, etico e indissolubile</strong> con il territorio e con le persone.
            </p>

            <div className="bg-[#8FE3DA]/10 rounded-xl p-6 border-l-4 border-[#8FE3DA]">
              <div className="flex items-start space-x-3">
                <Quote className="text-[#E67E22] flex-shrink-0" size={24} />
                <p className="text-gray-700 italic leading-relaxed">
                  In una cooperativa, ogni decisione è presa pensando al <strong>benessere collettivo</strong>, 
                  non al profitto di pochi. Questo è il nostro modo di fare assicurazione.
                </p>
              </div>
            </div>
          </section>

          {/* Back button */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap gap-4">
            <button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-[#0E8C84] to-[#0E8C84] text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Torna alla Home
            </button>
            <button
              onClick={() => navigate('/societa-benefit')}
              className="bg-white text-[#0E8C84] border-2 border-[#0E8C84] px-8 py-3 rounded-lg font-semibold hover:bg-[#0E8C84]/5 transition-all duration-300"
            >
              ← Scopri l'Identità Benefit
            </button>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-[#10154F] text-white py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-white/70">
            © {new Date().getFullYear()} Assietica Società Cooperativa. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SocietaCooperativa;
